import type { StaffFeatureId } from "@wallyo/contracts/actions";
import { useCallback, useEffect, useReducer, useRef, useState, useSyncExternalStore } from "react";
import { getVerification, listVerifications } from "../../api/console-api";
import { useApi } from "../../api/context";
import { ConsoleApiError } from "../../api/errors";
import { createIntent } from "../../api/idempotency";
import { t } from "../../i18n";
import { useHash } from "../../navigation/use-hash";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { PageTop } from "../../shell/PageTop";
import { useStaff } from "../../shell/staff-context";
import { sendApprove, sendReject } from "./decision";
import { DetailView, VIEW_ID_IMAGES, type Outcome } from "./DetailView";
import { dialogReducer, type DialogEvent } from "./dialog-state";
import { createImageViewer } from "./image-viewer";
import { INITIAL_QUEUE_STATE, queueReducer } from "./queue-state";
import { QueueView, queueSummary } from "./QueueView";
import type { VerificationDetail } from "./verification-format";
import { listRequest, parseVerificationsHash, verificationsHref, type VerificationFilters } from "./verification-route";

const FEATURES = {
  images: VIEW_ID_IMAGES,
  approve: "APPROVE_PROS_STAFF_FEATURE_ENABLEMENT",
} as const satisfies Record<string, StaffFeatureId>;

/**
 * Vérifications (REVIEW_VERIFICATIONS, checked by the shell). `#verifications`
 * is the queue, `#verifications/<id>` opens one next to it. The server checks
 * every feature again on every request; these only decide what to offer.
 */
export function VerificationsScreen() {
  const hash = useHash();
  const { me } = useStaff();
  const { client } = useApi();
  const route = parseVerificationsHash(hash);
  const { filters, selectedId } = route;
  const has = (feature: StaffFeatureId) => me.features.includes(feature);

  const [queue, dispatch] = useReducer(queueReducer, INITIAL_QUEUE_STATE);
  const [reloads, reloadQueue] = useReducer((count: number) => count + 1, 0);
  // Answers for filters no longer on screen are dropped.
  const generation = useRef(0);
  const filtersKey = verificationsHref(filters);

  useEffect(() => {
    const mine = ++generation.current;
    dispatch({ type: "load" });
    listVerifications(client, listRequest(filters)).then(
      (page) => generation.current === mine && dispatch({ type: "loaded", page }),
      (error: unknown) => generation.current === mine && dispatch({ type: "failed", error }),
    );
    // `filtersKey` stands for `filters`, whose object changes on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, filtersKey, reloads]);

  const loadMore = useCallback(() => {
    if (!queue.nextCursor || queue.loadingMore) return;
    const mine = generation.current;
    dispatch({ type: "loadMore" });
    listVerifications(client, listRequest(filters, queue.nextCursor)).then(
      (page) => generation.current === mine && dispatch({ type: "loadedMore", page }),
      (error: unknown) => generation.current === mine && dispatch({ type: "moreFailed", error }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, filtersKey, queue.nextCursor, queue.loadingMore]);

  const onFilters = useCallback(
    (next: VerificationFilters) => {
      window.location.hash = verificationsHref(next, selectedId);
    },
    [selectedId],
  );

  return (
    <>
      <PageTop title={t("screen.verifications")} sub={queueSummary(filters, queue)} />
      <div className="split list">
        <QueueView
          filters={filters}
          selectedId={selectedId}
          queue={queue}
          onFilters={onFilters}
          onRetry={reloadQueue}
          onLoadMore={loadMore}
        />
        {selectedId ? (
          // Keyed by the verification: another one starts from nothing, images not loaded.
          <Detail
            key={selectedId}
            identityVerificationId={selectedId}
            filters={filters}
            canViewImages={has(FEATURES.images)}
            canApprove={has(FEATURES.approve)}
            roles={me.staffMember.roles.map((role) => role.name)}
            onDecided={reloadQueue}
          />
        ) : (
          <div className="card">
            <p className="foot">{t("verifications.detail.pick")}</p>
          </div>
        )}
      </div>
    </>
  );
}

type DetailState =
  | { status: "loading" }
  | { status: "ready"; verification: VerificationDetail }
  | { status: "failed"; error: unknown };

function Detail({
  identityVerificationId,
  filters,
  canViewImages,
  canApprove,
  roles,
  onDecided,
}: {
  identityVerificationId: string;
  filters: VerificationFilters;
  canViewImages: boolean;
  canApprove: boolean;
  roles: string[];
  onDecided: () => void;
}) {
  const { client } = useApi();
  const [state, setState] = useState<DetailState>({ status: "loading" });
  const [reloads, reload] = useReducer((count: number) => count + 1, 0);
  // The image URLs live in this object and nowhere else: gone when this verification is left.
  const [viewer] = useState(() => createImageViewer({ client, identityVerificationId, canView: canViewImages }));
  const images = useSyncExternalStore(viewer.subscribe, viewer.get);
  useEffect(() => () => viewer.dispose(), [viewer]);

  const [dialog, dispatchDialog] = useReducer(dialogReducer, null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const intent = useRef(createIntent());
  const inFlight = useRef(false);

  useEffect(() => {
    let current = true;
    // A reload keeps what is on screen until the new answer replaces it.
    setState((previous) => (previous.status === "ready" ? previous : { status: "loading" }));
    getVerification(client, identityVerificationId).then(
      ({ verification }) => current && setState({ status: "ready", verification }),
      (error: unknown) => current && setState({ status: "failed", error }),
    );
    return () => {
      current = false;
    };
  }, [client, identityVerificationId, reloads]);

  const verification = state.status === "ready" ? state.verification : null;

  const onDialog = useCallback(
    (event: DialogEvent) => {
      if (event.type !== "submit") {
        // Opening, or changing what is asked for, is a new intent.
        if (event.type !== "close") intent.current.reset();
        if (event.type === "open") setOutcome(null);
        dispatchDialog(event);
        return;
      }
      // One click, one request: a second click while it is on its way does nothing.
      if (inFlight.current || !verification) return;
      const next = dialogReducer(dialog, event);
      dispatchDialog(event);
      if (!next?.pending) return;
      inFlight.current = true;
      const key = intent.current.key();
      const done =
        next.kind === "approve"
          ? sendApprove(client, verification, next.reason, key).then((): Outcome => ({ kind: "approve" }))
          : sendReject(
              client,
              verification,
              // The dialog refuses to submit a rejection without a code.
              { reasonCode: next.reasonCode ?? "other", note: next.reason },
              key,
            ).then((): Outcome => ({ kind: "reject" }));
      done
        .then(
          (result) => {
            // Never optimistic: the decision shows only now, from the server's answer.
            intent.current.reset();
            dispatchDialog({ type: "succeeded" });
            setOutcome(result);
            reload();
            onDecided();
          },
          (error: unknown) => dispatchDialog({ type: "failed", error }),
        )
        .finally(() => {
          inFlight.current = false;
        });
    },
    [client, verification, dialog, onDecided],
  );

  const onStaleReload = useCallback(() => {
    intent.current.reset();
    dispatchDialog({ type: "close" });
    reload();
    onDecided();
  }, [onDecided]);

  const onShowImages = useCallback(() => void viewer.show(), [viewer]);

  if (!verification) {
    return (
      <div className="card">
        {state.status === "failed" ? (
          <div className="audit-error">
            <p className="foot">
              {state.error instanceof ConsoleApiError && state.error.status === 404
                ? t("verifications.detail.notFound")
                : t("verifications.detail.loadFailed")}
            </p>
            <ErrorNotice error={state.error} />
            <div>
              <button className="btn primary" type="button" onClick={reload}>
                {t("audit.retry")}
              </button>
            </div>
          </div>
        ) : (
          <div className="empty" role="status">
            <span className="spinner" aria-hidden="true" />
            <span>{t("verifications.detail.loading")}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <DetailView
      verification={verification}
      filters={filters}
      images={images}
      canApprove={canApprove}
      roles={roles}
      dialog={dialog}
      outcome={outcome}
      onShowImages={onShowImages}
      onHideImages={viewer.hide}
      onDialog={onDialog}
      onReload={onStaleReload}
    />
  );
}
